import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { DatePicker, Card } from "antd";
import dayjs from "dayjs";
import CustomerDashboard from "./CustomerDashboard";
import BigTable from "./BigTable";
import CRMTable from "./CRMTable";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import Layout from '../../Layout'
import { CartesianGrid } from "recharts";
import { useState } from "react";

const { RangePicker } = DatePicker;

export default function Dashboard() {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    // ================= MOCK DATA =================
    const onlineData = [
        { name: "Q1", value: 1206 },
        { name: "Q2", value: 12 },
    ];

    const salesData = [
        { name: t('sale_online.new'), Q1: 42, Q2: 11 },
        { name: "Upsale", Q1: 3, Q2: 0 },
        { name: t('sale_online.renewal'), Q1: 1, Q2: 0 },
        { name: t('sale_online.leave_number'), Q1: 0, Q2: 1 },
    ];

    const tableData = [
        {
            key: "Q1",
            name: "Q1",
            t3_revenue: 1867692000,
            t3_customer: 46,
            t2_revenue: 4281974000,
            t2_customer: 77,
            t1_revenue: 4974399848,
            t1_customer: 121,
        },
        {
            key: "Q2",
            name: "Q2",
            t3_revenue: 226600000,
            t3_customer: 12,
            t2_revenue: 1306035000,
            t2_customer: 23,
            t1_revenue: 1516395000,
            t1_customer: 48,
        },
    ];

    const formatMoney = (num: number) =>
        num?.toLocaleString("vi-VN");

    // ================= RENDER =================
    return (
        <Layout>
            <Helmet>
                <title>{t('page.sale_online_report')} | Khothi</title>
            </Helmet>
            <div className="space-y-4">
                <div className="flex gap-4 items-center">
                    <h2 className="text-xl font-bold text-gray-900 text-000000e0">
                        {t('sale_online.title')}
                    </h2>
                    <div
                        onClick={() => setIsOpen(!isOpen)}
                        className={!isOpen ? "h-[32px] px-4 rounded-[4px] bg-[#3E91AB] text-white text-[14px] flex items-center cursor-pointer" : "h-[32px] px-4 rounded-[4px] bg-gray-200 text-000000e0 text-[14px] flex items-center cursor-pointer"}
                    >
                        {t('page_report.chart')}
                    </div>
                    <div
                        onClick={() => setIsOpen(!isOpen)}
                        className={isOpen ? "h-[32px] px-4 rounded-[4px] bg-[#3E91AB] text-white text-[14px] flex items-center cursor-pointer" : "h-[32px] px-4 rounded-[4px] bg-gray-200 text-000000e0 text-[14px] flex items-center cursor-pointer"}
                    >
                        {t('page_report.list')}
                    </div>
                </div>
                {!isOpen ? (
                    <div className="space-y-4">
                        {/* FILTER */}
                        <Card
                            title={t('sale_online.select_date_range')}
                            className="flex-1 dark:bg-[#141414]"
                        >
                            <RangePicker
                                defaultValue={[dayjs().startOf("month"), dayjs()]}
                            />
                        </Card>
                        <Card className="dark:bg-[#1f1f1f]">
                            <div className="flex flex-col lg:flex-row gap-4 items-center">
                                {/* Chart wrapper */}
                                <div className="flex flex-col lg:flex-row gap-4 w-full">
                                    {/* Chart 1 */}
                                    <Card
                                        title={t('sale_online.online_visitors')}
                                        className="flex-1 dark:bg-[#141414]"
                                    >
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart
                                                data={onlineData}
                                                margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                                            >
                                                <Legend verticalAlign="top" align="center" />
                                                <XAxis
                                                    dataKey="name"
                                                    padding={{ left: 0, right: 0 }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    width={30} // hoặc 0 nếu muốn sát hẳn
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <Tooltip />
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <Bar
                                                    dataKey="value"
                                                    name={t('sale_online.visitors')}
                                                    fill="#4A90E2"
                                                    radius={[6, 6, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>

                                    {/* Chart 2 */}
                                    <Card
                                        title={t('sale_online.visitors_to_branch')}
                                        className="flex-1 dark:bg-[#141414]"
                                    >
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={salesData}>
                                                <Legend verticalAlign="top" align="center" />
                                                <XAxis dataKey="name" padding={{ left: 0, right: 0 }} />
                                                <YAxis
                                                    width={30} // hoặc 0 nếu muốn sát hẳn
                                                    axisLine={false}
                                                    tickLine={false}
                                                />
                                                <Tooltip />
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <Bar
                                                    dataKey="Q1"
                                                    name="Q1"
                                                    fill="#2F80ED"
                                                    radius={[6, 6, 0, 0]}
                                                />
                                                <Bar
                                                    dataKey="Q2"
                                                    name="Q2"
                                                    fill="#BDBDBD"
                                                    radius={[6, 6, 0, 0]}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Card>
                                </div>
                            </div>
                        </Card>

                        {/* TABLE */}
                        <Card
                            title={t('sale_online.customers_by_service_group')}
                            className="dark:bg-[#1f1f1f]"
                        >
                            <div className="overflow-x-auto">
                                <table className="w-full border text-sm">
                                    <thead>
                                        <tr className="bg-gray-100 dark:bg-[#2a2a2a] text-000000e0">
                                            <th className="border p-2">{t('sale_online.branch')}</th>
                                            <th className="border p-2" colSpan={2}>
                                                {t('sale_online.month_3')}
                                            </th>
                                            <th className="border p-2" colSpan={2}>
                                                {t('sale_online.month_2')}
                                            </th>
                                            <th className="border p-2" colSpan={2}>
                                                {t('sale_online.month_1')}
                                            </th>
                                        </tr>

                                        <tr className="bg-gray-50 dark:bg-[#222] text-000000e0">
                                            <th className="border p-2"></th>
                                            <th className="border p-2">{t('sale_online.revenue')}</th>
                                            <th className="border p-2">{t('sale_online.customer_visits')}</th>
                                            <th className="border p-2">{t('sale_online.revenue')}</th>
                                            <th className="border p-2">{t('sale_online.customer_visits')}</th>
                                            <th className="border p-2">{t('sale_online.revenue')}</th>
                                            <th className="border p-2">{t('sale_online.customer_visits')}</th>
                                        </tr>
                                    </thead>

                                    <tbody className="text-000000e0">
                                        {tableData.map((item) => (
                                            <tr key={item.key} className="text-center">
                                                <td className="border p-2 font-medium">
                                                    {item.name}
                                                </td>

                                                <td className="border p-2">
                                                    {formatMoney(item.t3_revenue)}
                                                </td>
                                                <td className="border p-2">
                                                    {item.t3_customer}
                                                </td>

                                                <td className="border p-2">
                                                    {formatMoney(item.t2_revenue)}
                                                </td>
                                                <td className="border p-2">
                                                    {item.t2_customer}
                                                </td>

                                                <td className="border p-2">
                                                    {formatMoney(item.t1_revenue)}
                                                </td>
                                                <td className="border p-2">
                                                    {item.t1_customer}
                                                </td>
                                            </tr>
                                        ))}

                                        {/* TOTAL */}
                                        <tr className="font-bold bg-gray-100 dark:bg-[#2a2a2a] text-center">
                                            <td className="border p-2">{t('sale_online.total')}</td>
                                            <td className="border p-2">
                                                2.094.292.000
                                            </td>
                                            <td className="border p-2">58</td>
                                            <td className="border p-2">
                                                5.588.009.000
                                            </td>
                                            <td className="border p-2">100</td>
                                            <td className="border p-2">
                                                6.490.794.848
                                            </td>
                                            <td className="border p-2">169</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        <CustomerDashboard />

                        <BigTable />
                    </div>
                ) : (
                    <CRMTable />
                )}
            </div>
        </Layout>
    );
}